import { Injectable } from '@angular/core';
import {
	LookupClient,
	FieldWorkerType,
	EmployeeStatus,
	PersonnelClient,
	FieldTeam,
	FormType,
	FilledOutFormStatus,
	CustomerClient,
	AdministratorDetails,
	Country,
	RailroadLink,
	FieldWorkerLink,
	Region,
	ServiceType,
} from './api';
import { BehaviorSubject, Observable } from 'rxjs';
import { shareReplay, map, switchMap } from 'rxjs/operators';
import { getAllListArgs } from './constants';

@Injectable({
	providedIn: 'root',
})
export class LookUpsService {
	private _refresh = new BehaviorSubject<void>(null);

	private _fieldWorkerTypes = this._refresh.pipe(
		switchMap(() => this.lookupsClient.getFieldWorkerTypes()),
		shareReplay(1)
	);

	private _employeeStatuses = this._refresh.pipe(
		switchMap(() => this.lookupsClient.getEmployeeStatuses()),
		shareReplay(1)
	);

	private _formTypes = this._refresh.pipe(
		switchMap(() => this.lookupsClient.getFormTypes()),
		shareReplay(1)
	);

	private _formStatuses = this._refresh.pipe(
		switchMap(() => this.lookupsClient.getFilledOutFormStatuses()),
		shareReplay(1)
	);

	private _serviceTypes = this._refresh.pipe(
		switchMap(() => this.lookupsClient.getServiceTypes()),
		shareReplay(1)
	);

	private _railroadLogos = this._refresh.pipe(
		switchMap(() => this.customerClient.getRailroadLogos()),
		shareReplay(1)
	);

	private _adminDetails = this._refresh.pipe(
		switchMap(() => this.lookupsClient.getAdministratorDetails()),
		shareReplay(1)
	);

	private _fieldTeams = this._refresh.pipe(
		switchMap(() => this.personnelClient.getTeams(getAllListArgs)),
		map((pagedResults) => pagedResults.results),
		shareReplay(1)
	);

	private _railroads = this._refresh.pipe(
		switchMap(() => this.customerClient.getRailRoadLinks()),
		map((railroads) =>
			railroads.sort((a, b) =>
				a.name.toUpperCase().localeCompare(b.name.toUpperCase())
			)
		),
		shareReplay(1)
	);

	private _fieldManagers = this._refresh.pipe(
		switchMap(() => this.personnelClient.getFieldManagersPickList()),
		map((fieldManagers) =>
			fieldManagers.sort((a, b) =>
				a.name.toUpperCase().localeCompare(b.name.toUpperCase())
			)
		),
		shareReplay(1)
	);

	private _countries = this._refresh.pipe(
		switchMap(() => this.lookupsClient.getCountries()),
		shareReplay(1)
	);

	// Regions are static reference data, cached once per country code.
	private readonly _regionsByCountry = new Map<string, Observable<Region[]>>();

	constructor(
		private readonly lookupsClient: LookupClient,
		private readonly personnelClient: PersonnelClient,
		private readonly customerClient: CustomerClient
	) { }

	get fieldWorkerTypes(): Observable<FieldWorkerType[]> {
		return this._fieldWorkerTypes;
	}

	get employeeStatuses(): Observable<EmployeeStatus[]> {
		return this._employeeStatuses;
	}

	get formTypes(): Observable<FormType[]> {
		return this._formTypes;
	}

	get formStatuses(): Observable<FilledOutFormStatus[]> {
		return this._formStatuses;
	}

	get serviceTypes(): Observable<ServiceType[]> {
		return this._serviceTypes;
	}

	get railroadLogos(): Observable<{ [key: string]: string }> {
		return this._railroadLogos;
	}

	get adminDetails(): Observable<AdministratorDetails> {
		return this._adminDetails;
	}

	get fieldTeams(): Observable<FieldTeam[]> {
		return this._fieldTeams;
	}

	get railroads(): Observable<RailroadLink[]> {
		return this._railroads;
	}

	get fieldManagers(): Observable<FieldWorkerLink[]> {
		return this._fieldManagers;
	}

	get countries(): Observable<Country[]> {
		return this._countries;
	}

	regionsForCountry(countryCode: string): Observable<Region[]> {
		if (!this._regionsByCountry.has(countryCode)) {
			this._regionsByCountry.set(
				countryCode,
				this.lookupsClient
					.getRegionsForCountry(countryCode)
					.pipe(shareReplay(1))
			);
		}
		return this._regionsByCountry.get(countryCode);
	}

	refreshLookups() {
		this._regionsByCountry.clear();
		this._refresh.next();
	}
}
